import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { combineMemberProducts, splitMemberProducts } from "./brand-data";

export type AdminAuthState = {
  session: Session | null;
  isAdmin: boolean;
  role: AdminRole | null;
};

export type AdminRole = "admin" | "system_admin";
export type MemberAccountStatus = "active" | "inactive" | "banned" | "under_review" | "archived";
export type MemberPlan = "basic" | "pro" | "premium";

export type LiveMemberProfile = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  mobileNumber: string;
  mobileNotificationsConsent: boolean;
  accountStatus: MemberAccountStatus;
  plan: MemberPlan;
  emailVerified: boolean;
  mobileVerified: boolean;
  district: string;
  categories: string[];
  products: string[];
  brands: string[];
  frequency: string;
  spendRange: string;
  priceInterest: string;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  createdAt: string;
};

export type MemberRegistrationInput = Omit<LiveMemberProfile, "id" | "createdAt" | "avatarUrl" | "mobileNumber" | "mobileNotificationsConsent" | "accountStatus" | "plan" | "emailVerified" | "mobileVerified" | "brands"> & { avatarUrl?: string; brands?: string[] };

export type MemberProfileUpdateInput = Pick<LiveMemberProfile, "firstName" | "lastName" | "district" | "avatarUrl">;

export type PublicProductDemand = {
  product: string;
  memberCount: number;
};

export type PublicCommunityMetrics = {
  activeMemberCount: number;
  highDemandProductCount: number;
  uniqueBrandCount: number;
  uniqueProductCount: number;
  totalBrandSelections: number;
  totalProductSelections: number;
};

export type AuditEvent = {
  id: number;
  occurredAt: string;
  actorUserId: string | null;
  actorEmail: string;
  actorRole: string;
  action: "insert" | "update" | "delete";
  entityType: string;
  entityId: string;
  outcome: string;
  source: string;
  changedFields: string[];
  beforeData: Record<string, unknown> | null;
  afterData: Record<string, unknown> | null;
};

export type ManagedAdminUser = {
  email: string;
  userId: string | null;
  role: AdminRole;
  createdAt: string;
};

type MemberProfileRow = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url: string;
  mobile_number: string | null;
  mobile_notifications_consent: boolean;
  account_status: MemberAccountStatus;
  membership_plan: MemberPlan;
  email_verified_at: string | null;
  mobile_verified_at: string | null;
  district: string;
  categories: string[] | null;
  products: string[] | null;
  frequency: string;
  spend_range: string;
  price_interest: string;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  created_at: string;
};

function normalizeMember(row: MemberProfileRow): LiveMemberProfile {
  const selections = splitMemberProducts(row.products ?? []);
  return {
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email,
    avatarUrl: row.avatar_url ?? "",
    mobileNumber: row.mobile_number ?? "",
    mobileNotificationsConsent: row.mobile_notifications_consent ?? false,
    accountStatus: row.account_status ?? "under_review",
    plan: row.membership_plan ?? "basic",
    emailVerified: Boolean(row.email_verified_at),
    mobileVerified: Boolean(row.mobile_verified_at),
    district: row.district,
    categories: row.categories ?? [],
    products: selections.products,
    brands: selections.brands,
    frequency: row.frequency,
    spendRange: row.spend_range,
    priceInterest: row.price_interest,
    latitude: row.latitude,
    longitude: row.longitude,
    accuracy: row.accuracy,
    createdAt: row.created_at,
  };
}

export async function getAdminAuthState(session?: Session | null): Promise<AdminAuthState> {
  const activeSession = session ?? (await supabase.auth.getSession()).data.session;
  if (!activeSession) return { session: null, isAdmin: false, role: null };
  const activeEmail = activeSession.user.email?.trim().toLowerCase();
  if (!activeEmail) return { session: activeSession, isAdmin: false, role: null };

  const { data, error } = await supabase
    .from("admin_users")
    .select("email,role")
    .eq("email", activeEmail)
    .maybeSingle();

  if (error) throw error;
  const role: AdminRole | null = data?.role === "system_admin" ? "system_admin" : data ? "admin" : null;
  return { session: activeSession, isAdmin: Boolean(data), role };
}

export async function signInAdmin(email: string, password: string): Promise<AdminAuthState> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  const state = await getAdminAuthState(data.session);
  if (!state.isAdmin) {
    await supabase.auth.signOut();
    throw new Error("This account does not have administrator access.");
  }
  return state;
}

export async function signInWithGoogle(destination: "admin" | "member") {
  const redirectUrl = new URL(window.location.origin + window.location.pathname);
  const currentParams = new URLSearchParams(window.location.search);
  const theme = currentParams.get("theme");
  const language = currentParams.get("lang");
  if (theme) redirectUrl.searchParams.set("theme", theme);
  if (language) redirectUrl.searchParams.set("lang", language);
  redirectUrl.searchParams.set("auth-return", destination);
  window.sessionStorage.setItem("kitanego-auth-return", destination);

  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: redirectUrl.toString(),
      queryParams: { prompt: "select_account" },
    },
  });
  if (error) {
    window.sessionStorage.removeItem("kitanego-auth-return");
    throw error;
  }
}

export async function signInMember(email: string, password: string) {
  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.set("auth-return", "member");
  window.history.replaceState(window.history.state, "", `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    const cleaned = new URL(window.location.href);
    cleaned.searchParams.delete("auth-return");
    window.history.replaceState(window.history.state, "", `${cleaned.pathname}${cleaned.search}${cleaned.hash}`);
    throw error;
  }
  await requireActiveMember();
}

export class MemberAccountStatusError extends Error {
  status: MemberAccountStatus;
  constructor(status: MemberAccountStatus) {
    super(status === "archived" ? "This member account has been archived." : status === "banned" ? "This member account has been banned." : status === "inactive" ? "This member account is inactive." : "This member account is under review.");
    this.status = status;
  }
}

export async function requireActiveMember() {
  const { data, error } = await supabase.rpc("get_current_member_access");
  if (error) throw error;
  const access = Array.isArray(data) ? data[0] : data;
  if (!access) return null;
  const status = String(access.account_status) as MemberAccountStatus;
  if (status !== "active") {
    await supabase.auth.signOut();
    throw new MemberAccountStatusError(status);
  }
  return status;
}

export async function sendMemberRegistrationLink(email: string, firstName: string, lastName: string) {
  const redirectUrl = new URL(window.location.origin + window.location.pathname);
  const currentParams = new URLSearchParams(window.location.search);
  const theme = currentParams.get("theme");
  if (theme) redirectUrl.searchParams.set("theme", theme);
  redirectUrl.searchParams.set("auth-return", "registration-password");

  const randomValues = new Uint32Array(8);
  window.crypto.getRandomValues(randomValues);
  const temporaryPassword = `${Array.from(randomValues, (value) => value.toString(36)).join("")}Aa1!`;

  const { error } = await supabase.auth.signUp({
    email,
    password: temporaryPassword,
    options: {
      emailRedirectTo: redirectUrl.toString(),
      data: {
        given_name: firstName,
        family_name: lastName,
        full_name: [firstName, lastName].filter(Boolean).join(" "),
      },
    },
  });
  if (error) throw error;
}

export async function getMemberConfirmationSession(): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (data.session) return data.session;

  // Supports confirmation links that were issued before the email flow moved
  // away from PKCE. Supabase may already have exchanged this code during
  // client initialisation; only try explicitly when no session was created.
  const code = new URL(window.location.href).searchParams.get("code");
  if (!code) return null;

  const exchanged = await supabase.auth.exchangeCodeForSession(code);
  if (exchanged.error) return null;
  return exchanged.data.session;
}

export async function setMemberPassword(password: string) {
  const session = await getMemberConfirmationSession();
  if (!session) throw new Error("This confirmation link is invalid or has expired. Request a new link and try again.");

  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;

  const cleaned = new URL(window.location.href);
  cleaned.searchParams.delete("auth-return");
  cleaned.searchParams.delete("code");
  window.history.replaceState(window.history.state, "", `${cleaned.pathname}${cleaned.search}#/join`);
}

export async function sendAdminPasswordReset(email: string) {
  const redirectTo = `${window.location.origin}${window.location.pathname}?admin-recovery=1`;
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) throw error;
}

export async function updateAdminPassword(password: string) {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
  const url = new URL(window.location.href);
  url.searchParams.delete("admin-recovery");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}#/admin/login`);
}

export async function signOutAdmin() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function signOutMember() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function registerMember(input: MemberRegistrationInput): Promise<string> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const session = sessionData.session;
  const authEmail = session?.user.email?.trim().toLowerCase();
  if (!session || !authEmail) throw new Error("Your registration session has expired. Sign in again to finish your profile.");

  const { data, error } = await supabase.rpc("save_current_member_profile", {
    p_first_name: input.firstName,
    p_last_name: input.lastName,
    p_district: input.district,
    p_categories: input.categories,
    p_products: combineMemberProducts(input.products, input.brands ?? []),
    p_frequency: input.frequency,
    p_spend_range: input.spendRange,
    p_price_interest: input.priceInterest,
    p_latitude: input.latitude,
    p_longitude: input.longitude,
    p_accuracy: input.accuracy,
    p_avatar_url: input.avatarUrl ?? "",
  });

  if (error) throw error;
  if (!data) throw new Error("Your profile could not be created. Please try again.");
  await requireActiveMember();
  return authEmail;
}

export async function loadCurrentMemberProfile(): Promise<LiveMemberProfile | null> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) return null;

  await claimCurrentMemberProfile();
  await requireActiveMember();

  const { data, error } = await supabase
    .from("member_profiles")
    .select("id,first_name,last_name,email,avatar_url,mobile_number,mobile_notifications_consent,account_status,membership_plan,email_verified_at,mobile_verified_at,district,categories,products,frequency,spend_range,price_interest,latitude,longitude,accuracy,created_at")
    .eq("auth_user_id", session.user.id)
    .maybeSingle();

  if (error) throw error;
  return data ? normalizeMember(data as MemberProfileRow) : null;
}

export async function loadAdminMemberProfiles(): Promise<LiveMemberProfile[]> {
  const { data, error } = await supabase
    .from("member_profiles")
    .select("id,first_name,last_name,email,avatar_url,mobile_number,mobile_notifications_consent,account_status,membership_plan,email_verified_at,mobile_verified_at,district,categories,products,frequency,spend_range,price_interest,latitude,longitude,accuracy,created_at")
    .neq("account_status", "archived")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data as MemberProfileRow[]).map(normalizeMember);
}

export async function updateMemberAccountStatus(profileId: string, status: MemberAccountStatus) {
  const { data, error } = await supabase.rpc("admin_update_member_account_status", { p_profile_id: profileId, p_status: status });
  if (error) throw error;
  return String(data) as MemberAccountStatus;
}

export async function archiveMemberProfile(profileId: string) {
  const { data, error } = await supabase.rpc("admin_archive_member_profile", { p_profile_id: profileId });
  if (error) throw error;
  return String(data) as "archived";
}

export async function updateMemberPlan(profileId: string, plan: MemberPlan) {
  const { data, error } = await supabase.rpc("admin_update_member_plan", { p_profile_id: profileId, p_plan: plan });
  if (error) throw error;
  return String(data) as MemberPlan;
}

export async function loadAuditEvents(limit = 250): Promise<AuditEvent[]> {
  const { data, error } = await supabase
    .from("audit_events")
    .select("id,occurred_at,actor_user_id,actor_email,actor_role,action,entity_type,entity_id,outcome,source,changed_fields,before_data,after_data")
    .order("occurred_at", { ascending: false })
    .limit(Math.min(Math.max(limit, 1), 500));

  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    id: Number(row.id),
    occurredAt: String(row.occurred_at ?? ""),
    actorUserId: row.actor_user_id ? String(row.actor_user_id) : null,
    actorEmail: String(row.actor_email ?? "System"),
    actorRole: String(row.actor_role ?? "system"),
    action: String(row.action ?? "update") as AuditEvent["action"],
    entityType: String(row.entity_type ?? "unknown"),
    entityId: String(row.entity_id ?? ""),
    outcome: String(row.outcome ?? "success"),
    source: String(row.source ?? "database"),
    changedFields: Array.isArray(row.changed_fields) ? row.changed_fields.map(String) : [],
    beforeData: row.before_data && typeof row.before_data === "object" ? row.before_data as Record<string, unknown> : null,
    afterData: row.after_data && typeof row.after_data === "object" ? row.after_data as Record<string, unknown> : null,
  }));
}

function normalizeManagedAdminUser(row: Record<string, unknown>): ManagedAdminUser {
  return {
    email: String(row.email ?? ""),
    userId: row.user_id ? String(row.user_id) : null,
    role: row.role === "system_admin" ? "system_admin" : "admin",
    createdAt: String(row.created_at ?? ""),
  };
}

export async function loadManagedAdminUsers(): Promise<ManagedAdminUser[]> {
  const { data, error } = await supabase.rpc("list_managed_admin_users");
  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map(normalizeManagedAdminUser);
}

export async function addWebAppAdmin(email: string): Promise<ManagedAdminUser> {
  const { data, error } = await supabase.rpc("add_web_app_admin", { p_email: email });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row !== "object") throw new Error("Administrator access could not be assigned.");
  return normalizeManagedAdminUser(row as Record<string, unknown>);
}

export async function removeWebAppAdmin(email: string): Promise<void> {
  const { data, error } = await supabase.rpc("remove_web_app_admin", { p_email: email });
  if (error) throw error;
  if (!data) throw new Error("This administrator could not be removed.");
}

export async function loadPublicProductDemand(): Promise<PublicProductDemand[]> {
  const { data, error } = await supabase.rpc("get_public_product_demand");
  if (error) throw error;
  return ((data ?? []) as Array<{ product: string; member_count: number | string }>).map((row) => ({
    product: row.product,
    memberCount: Number(row.member_count),
  }));
}

export async function loadPublicCommunityMetrics(): Promise<PublicCommunityMetrics> {
  const { data, error } = await supabase.rpc("get_public_community_metrics");
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    activeMemberCount: Number(row?.active_member_count ?? 0),
    highDemandProductCount: Number(row?.high_demand_product_count ?? 0),
    uniqueBrandCount: Number(row?.unique_brand_count ?? 0),
    uniqueProductCount: Number(row?.unique_product_count ?? 0),
    totalBrandSelections: Number(row?.total_brand_selections ?? 0),
    totalProductSelections: Number(row?.total_product_selections ?? 0),
  };
}

export async function claimCurrentMemberProfile(): Promise<string | null> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) return null;

  const { data, error } = await supabase.rpc("claim_member_profile");
  if (error) throw error;
  return data ? String(data) : null;
}

export async function getCurrentMemberAuthEmail(): Promise<string> {
  const session = (await supabase.auth.getSession()).data.session;
  return session?.user.email?.trim().toLowerCase() ?? "";
}

function normalizeMalaysianMobileNumber(value: string) {
  const compact = value.replace(/[\s()-]/g, "");
  const normalized = compact.startsWith("+60")
    ? compact
    : compact.startsWith("60")
      ? `+${compact}`
      : compact.startsWith("0")
        ? `+60${compact.slice(1)}`
        : `+60${compact}`;

  if (!/^\+601\d{8,9}$/.test(normalized)) {
    throw new Error("Enter a valid Malaysian mobile number, for example +60 12 345 6789.");
  }
  return normalized;
}

export async function updateCurrentMemberMobileContact(value: string, notificationsConsent: boolean): Promise<string> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error("Sign in again before adding your mobile number.");

  const phone = value.trim() ? normalizeMalaysianMobileNumber(value) : "";
  const { data, error } = await supabase.rpc("update_current_member_mobile_contact", {
    p_mobile_number: phone || null,
    p_notifications_consent: phone ? notificationsConsent : false,
  });
  if (error) throw error;
  if (!data) throw new Error("No member profile is linked to this account.");
  return phone;
}

export async function uploadCurrentMemberAvatar(blob: Blob, contentType: "image/jpeg" | "image/png"): Promise<string> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error("Sign in with Google again before updating your profile photo.");
  if (blob.size > 1024 * 1024) throw new Error("The resized profile photo must be 1 MB or smaller.");

  const profileId = await claimCurrentMemberProfile();
  if (!profileId) throw new Error("No member profile is linked to this Google account.");

  const extension = contentType === "image/png" ? "png" : "jpg";
  const path = `${session.user.id}/avatar.${extension}`;
  const { error } = await supabase.storage.from("member-avatars").upload(path, blob, {
    cacheControl: "3600",
    contentType,
    upsert: true,
  });
  if (error) throw error;

  const { data } = supabase.storage.from("member-avatars").getPublicUrl(path);
  return `${data.publicUrl}?v=${Date.now()}`;
}

export async function updateCurrentMemberProfile(input: MemberProfileUpdateInput): Promise<void> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error("Sign in with Google again before updating your profile.");

  const { data, error } = await supabase
    .rpc("update_current_member_profile", {
      p_first_name: input.firstName,
      p_last_name: input.lastName,
      p_district: input.district,
      p_avatar_url: input.avatarUrl,
    });

  if (error) throw error;
  if (!data) throw new Error("No member profile is linked to this Google account.");
}
